import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Tag, Space, Button, Divider, notification, Result, Spin } from 'antd';
import { ShoppingOutlined, DollarOutlined, CopyOutlined } from '@ant-design/icons';
import { database, ref, get } from '../pages/Auth/db/firebase';
import { GroceryCartProvider, useGroceryCart } from '../context/GroceryCartContext';
import ResizableSlideover from '../components/grocery/ResizableSlideover';

const { Title, Text } = Typography;

const ItemViewerInner = () => {
  const { facilityId, itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [facilityExists, setFacilityExists] = useState(null);
  const { addItem } = useGroceryCart();
  const [api, contextHolder] = notification.useNotification();

  // Set page title
  useEffect(() => {
    if (item) {
      document.title = `${item.name} - ${facilityId} | KioStock`;
    } else {
      document.title = `${facilityId} - Item Viewer | KioStock`;
    }
  }, [item, facilityId]);

  // ========== FACILITY VALIDATION ==========
  useEffect(() => {
    const checkFacility = async () => {
      if (!facilityId) {
        setFacilityExists(false);
        return;
      }

      try {
        const inventoryRef = ref(database, `inventory/${facilityId}`);
        const snapshot = await get(inventoryRef);
        
        if (snapshot.exists()) {
          setFacilityExists(true);
        } else {
          // Try case-insensitive match
          const allInventoryRef = ref(database, 'inventory');
          const allSnapshot = await get(allInventoryRef);
          
          if (allSnapshot.exists()) {
            const facilities = Object.keys(allSnapshot.val());
            const correctCase = facilities.find(f => f.toLowerCase() === facilityId.toLowerCase());
            
            if (correctCase && correctCase !== facilityId) {
              // Redirect to correct case
              navigate(`/${correctCase}/item/${itemId}`, { replace: true });
              return;
            }
          }
          
          setFacilityExists(false);
        }
      } catch (error) {
        console.error('Error checking facility:', error);
        setFacilityExists(false);
      }
    };

    checkFacility();
  }, [facilityId, itemId, navigate]);

  useEffect(() => {
    if (facilityExists === true) {
      loadItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, itemId, facilityExists]);

  const loadItem = async () => {
    if (!facilityId || !itemId) return;
    
    setLoading(true);
    try {
      const itemsRef = ref(database, `inventory/${facilityId}/items`);
      const snapshot = await get(itemsRef);
      
      if (snapshot.exists()) {
        const items = snapshot.val();
        
        // Check if itemId is an individual item ID (ends with a number)
        const isIndividualId = /\d+$/.test(itemId);
        let foundItem = null;
        
        if (isIndividualId) {
          // Extract base ID from individual ID (e.g., WH251005MA1 -> WH251005MA)
          const baseId = itemId.replace(/\d+$/, '');
          foundItem = Object.entries(items).find(([name, data]) => 
            data.baseId === baseId
          );
        } else {
          // Look for exact base ID or name match
          foundItem = Object.entries(items).find(([name, data]) => 
            data.baseId === itemId || name === itemId
          );
        }
        
        if (foundItem) {
          const itemData = {
            name: foundItem[0],
            ...foundItem[1]
          };
          
          // If it's an individual item, add individual ID info
          if (isIndividualId) {
            itemData.individualId = itemId;
            itemData.isIndividualItem = true;
            // Extract the individual number (e.g., 1 from WH251005MA1)
            const individualNumber = itemId.match(/\d+$/)[0];
            itemData.individualNumber = parseInt(individualNumber);
          }
          
          setItem(itemData);
        }
      }
    } catch (error) {
      console.error('Failed to load item:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyProductCode = () => {
    const codeToopy = item.isIndividualItem ? item.individualId : item.baseId;
    navigator.clipboard.writeText(codeToopy).then(() => {
      api.success({
        message: 'Code Copied!',
        description: `Product code "${codeToopy}" copied to clipboard. Paste it in the grocery list search bar.`,
      });
    }).catch(() => {
      api.error({
        message: 'Copy Failed',
        description: 'Unable to copy to clipboard',
      });
    });
  };

  const goToGroceryList = () => {
    navigate(`/${facilityId}/`);
  };

  const addCurrentToList = () => {
    if (!item) return;
    addItem(item);
    api.success({ message: 'Added', description: `${item.name} added to grocery list` });
  };

  // Show loading while checking facility
  if (facilityExists === null) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'var(--bg-primary)'
      }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  // Show error if facility doesn't exist
  if (facilityExists === false) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'var(--bg-primary)',
        padding: 'var(--spacing-xl)'
      }}>
        <Result
          status="404"
          title="Facility Not Found"
          subTitle={`The facility "${facilityId}" does not exist or has no inventory.`}
          extra={
            <Button type="primary" onClick={() => window.location.href = '/'}>
              Go to Login
            </Button>
          }
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'var(--bg-primary)'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!item) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: 'var(--bg-primary)',
        padding: 'var(--spacing-lg)'
      }}>
        <Card>
          <Title level={3}>Item Not Found</Title>
          <Text >The requested item could not be found.</Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="public-page" style={{ 
      minHeight: '100vh', 
      background: 'var(--bg-primary)',
      padding: 'var(--spacing-lg)',
      paddingRight: typeof window !== 'undefined' && window.innerWidth < 900 ? 'var(--spacing-lg)' : '380px'
    }}>
      {contextHolder}
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        {/* Item Details Card */}
        <Card style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg)' }}>
            <Title level={2}>{item.name}</Title>
            <Space>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                {item.baseId}
              </Tag>
              {item.isIndividualItem && (
                <Tag color="orange" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  Individual Item #{item.individualNumber}
                </Tag>
              )}
            </Space>
          </div>

          <Divider />

          <div style={{ marginBottom: 'var(--spacing-lg)' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong>Category:</Text>
                <Tag color="green">{item.category}</Tag>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>Price per Unit:</Text>
                <Text style={{ fontSize: '18px', color: 'var(--primary-color)' }}>
                  <DollarOutlined /> {item.price.toFixed(2)}
                </Text>
              </div>

              {item.createdAt && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text strong>Added:</Text>
                  <Text type="secondary">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </Text>
                </div>
              )}
            </Space>
          </div>

          <Divider />

          {/* Action Buttons */}
          <div style={{ textAlign: 'center' }}>
            <Space direction='vertical' size='middle' style={{ width: '100%' }}>
              <Button
                type='primary'
                size='large'
                icon={<ShoppingOutlined />}
                onClick={addCurrentToList}
                style={{ width: '250px' }}
              >
                Add to List
              </Button>
              
              <Button
                size='large'
                icon={<CopyOutlined />}
                onClick={copyProductCode}
                style={{ width: '250px' }}
              >
                Copy Product Code
              </Button>
              
              <Button
                size='large'
                onClick={goToGroceryList}
                style={{ width: '250px' }}
              >
                Go to Grocery List
              </Button>
            </Space>
          </div>
        </Card>

        {/* Instructions */}
        <Card size="small">
          <Title level={5} style={{ textAlign: 'center' }}>
            {item.isIndividualItem ? 'Individual Item Details:' : 'How to Add to Your Grocery List:'}
          </Title>
          {item.isIndividualItem ? (
            <div style={{ fontSize: '12px', textAlign: 'center' }}>
              <p style={{ margin: '8px 0' }}>
                This is individual item <strong>#{item.individualNumber}</strong> from the <strong>{item.name}</strong> group.
              </p>
              <p style={{ margin: '8px 0', color: 'var(--text-secondary)' }}>
                Individual items can have different discounts, locations, or special attributes.
              </p>
            </div>
          ) : (
            <ol style={{ fontSize: '12px', paddingLeft: '16px', margin: 0 }}>
              <li>Click "Copy Product Code" above</li>
              <li>Go to the grocery list page</li>
              <li>Paste the code in the search bar</li>
              <li>The item will be added to your list automatically</li>
            </ol>
          )}
        </Card>
      </div>
      <ResizableSlideover />
    </div>
  );
};

const PublicItemViewer = () => {
  const { facilityId } = useParams();
  return (
    <GroceryCartProvider facilityId={facilityId}>
      <ItemViewerInner />
    </GroceryCartProvider>
  );
};

export default PublicItemViewer;
